import { initializeApp } from 'firebase/app';
import {
  getAuth,
  connectAuthEmulator,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  linkWithPopup,
  signInAnonymously,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  User,
  UserCredential,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';

// Check if Firebase is enabled
const isFirebaseEnabled = import.meta.env.VITE_ENABLE_FIREBASE_AUTH === 'true';

/**
 * THE EMULATOR — and why the dev server defaults TO it, not away from it.
 *
 * Every social feature here (friendships, requests, presence, the Daily) is a
 * WRITE, and for most of this project's life the only place those writes could
 * land was production. `npm run dev` signed you into the real Firebase project.
 * So playing a test game on a laptop minted real accounts and real gameRecords,
 * sitting in the same collections as an actual family's actual Friday night.
 *
 * That is not a hypothetical. It happened three times. The last two cleanups
 * deleted 183 and then 76 accounts, and a family's leaderboard had names on it
 * like "Quick Fox" and "Cheery Cherry".
 *
 * An opt-in flag would not have prevented any of them — it existed, and every
 * single time, somebody simply didn't set it. A safety mechanism you have to
 * remember is a safety mechanism that fails on the day you're busy. So the
 * default is inverted: in dev, you get the emulator unless you explicitly say
 * otherwise, and the explicit thing is named so that nobody types it by
 * accident or by habit.
 *
 * Production builds are untouched — `import.meta.env.DEV` is false there, and
 * this whole branch compiles out.
 *
 * Two spellings of the opt-in are honoured because two people independently
 * added emulator support and both names now live in config files and docs; a
 * flag that only works if you guess the right synonym is a flag that sends the
 * next person back to production.
 */
const emulatorRequested =
  import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true' ||
  import.meta.env.VITE_FIREBASE_EMULATOR === 'true';

/** The deliberate act. Long, ugly, and impossible to type by muscle memory. */
const prodFromDevAllowed = import.meta.env.VITE_ALLOW_PRODUCTION_FROM_DEV === 'true';

const useEmulator = emulatorRequested || (import.meta.env.DEV && !prodFromDevAllowed);

if (import.meta.env.DEV && !useEmulator) {
  console.warn(
    '%c⚠️  THIS DEV SERVER IS SIGNED INTO PRODUCTION FIREBASE.\n' +
      'Every account you create and every game you play is real, and lands in real ' +
      'users\' collections. You asked for this with VITE_ALLOW_PRODUCTION_FROM_DEV=true.',
    'color:#fff;background:#b00020;font-weight:bold;padding:4px'
  );
}

/**
 * What happened when a guest pressed "Sign in with Google".
 *
 *   linked-preserved-uid       the guest uid survived — history intact (the goal)
 *   existing-account-signed-in that Google account already existed here; we
 *                              signed into it, so the guest uid is abandoned
 *   fresh-sign-in              they were not a guest to begin with
 */
export type GoogleLinkOutcome =
  | 'linked-preserved-uid'
  | 'existing-account-signed-in'
  | 'fresh-sign-in';

// Firebase configuration - only initialize if enabled and config is valid
let app: any = null;
let auth: any = null;
let db: any = null;

if (isFirebaseEnabled) {
  const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID
  };

  try {
    console.log('🔥 Initializing Firebase...');
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    db = getFirestore(app);
    // The same client code, the same firestore.rules, against a local emulator —
    // so a broken rule or a rejected write shows up on a laptop instead of in a
    // bug report. Ports are the Firebase defaults; see firebase.json.
    if (useEmulator) {
      connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
      connectFirestoreEmulator(db, '127.0.0.1', 8080);
      console.warn('🧪 Firebase pointed at LOCAL EMULATORS — no production data is reachable.');
    }
    console.log('✅ Firebase initialized successfully');
  } catch (error) {
    console.error('❌ Firebase initialization failed:', error);
    app = null;
    auth = null;
    db = null;
  }
} else {
  console.log('🚫 Firebase disabled via environment variable');
}

// Google Auth Provider - only create if Firebase is enabled
let googleProvider: any = null;
if (isFirebaseEnabled && auth) {
  googleProvider = new GoogleAuthProvider();
  googleProvider.setCustomParameters({
    prompt: 'select_account'
  });
}

export interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  /**
   * The EFFECTIVE avatar — the thing every consumer renders. It is a
   * derived value: either a photo URL (when `useGooglePhoto` is on and a
   * `photoURL` exists) or a branded avatar id like 'fox'.
   *
   * It stays a single denormalized field on purpose. GameContext, App.tsx,
   * the socket handshake and every gameRecord read `profile.avatar` and
   * nothing else — so writing the resolved value here means the Google
   * photo flows all the way through to the results screen with no changes
   * at any of those call sites. Use `reconcileAvatarFields()` to compute it;
   * never hand-write it.
   */
  avatar: string;
  createdAt: Date;
  lastLoginAt: Date;

  /**
   * Mirror of the Google account photo, refreshed on every login.
   *
   * This exists because `avatar` used to be the ONLY field, doing
   * double-duty for both a branded id and a photo URL. The moment the
   * setup screen wrote its default ('fox'), the Google photo was gone —
   * unrecoverable, because we'd never stored it anywhere. That is the
   * "my profile icon doesn't show" bug: not a render failure, a
   * *storage* failure. Keeping the photo in its own field means a user's
   * branded pick and their Google photo can coexist, and the toggle
   * between them is lossless in both directions.
   */
  photoURL?: string;

  /** The user's branded avatar pick (e.g. 'fox'), preserved even while the
   *  Google photo is being displayed. */
  brandedAvatar?: string;

  /** Show the Google photo instead of the branded avatar. Undefined means
   *  "never chosen" and is treated as TRUE whenever a photoURL exists —
   *  if you signed in with Google, seeing your own face is the behaviour
   *  you expect, and it's the behaviour the Settings toggle has been
   *  claiming all along. Picking a branded avatar sets this to false. */
  useGooglePhoto?: boolean;

  /** Account classification — drives the tiny AI badge on Atom's
   *  profile, lets us cleanly clean up demo data before launch, and
   *  separates real human accounts from automated test fixtures.
   *  Defaults to 'human' if missing on read. */
  accountType?: 'human' | 'ai' | 'test';

  /** Demo Mode flag. Set on every seeded fixture (players + games)
   *  so a single Settings toggle can hide them without losing them
   *  in the database. Real human users set this to false (or omit). */
  isDemo?: boolean;

  /** Subscription tier — everyone is on 'free' today. The Plus tiers
   *  are visible in the UI but not yet billable; users see a beta
   *  modal explaining the no-charge window. */
  subscriptionTier?: 'free' | 'plus_monthly' | 'plus_annual' | 'family';

  /** User-authored bio shown on profile + leaderboard rows. */
  bio?: string;

  /** Privacy controls — single source of truth read by lobby + profile +
   *  leaderboard. All optional with safe defaults baked into readers. */
  privacy?: {
    /** Show online presence on the public home/leaderboard. Default true.
     *  When false, friends still see you online; strangers don't. */
    showOnlineToPublic?: boolean;
    /** Show me on public leaderboards. Default true. */
    showOnLeaderboard?: boolean;
    /** Default game visibility ('public' | 'private') for createRoom. */
    defaultGameVisibility?: 'public' | 'private';
    /** Whether finished public games surface my username (vs. anon). */
    publicizeMyGames?: boolean;
    /** Anyone can send friend requests vs. only via shared games. */
    allowFriendRequests?: boolean;
  };

  preferences: {
    soundEnabled: boolean;
    notificationsEnabled: boolean;
    theme: 'light' | 'dark' | 'auto';
    aiDifficulty: 'easy' | 'medium' | 'hard' | 'expert';
    aiPersonality: 'encouraging' | 'witty' | 'competitive' | 'friendly';
  };
  stats: {
    totalGames: number;
    totalWins: number;
    totalScore: number;
    currentStreak: number;
    longestStreak: number;
    level: number;
    experiencePoints: number;
  };
  achievements: string[];
}

class FirebaseAuthService {
  private currentUser: User | null = null;
  private authStateListeners: ((user: User | null) => void)[] = [];

  constructor() {
    if (isFirebaseEnabled && auth) {
      // Listen for auth state changes
      onAuthStateChanged(auth, (user) => {
        this.currentUser = user;
        this.authStateListeners.forEach(listener => listener(user));
        
        if (user) {
          console.log('🔥 Firebase Auth: User signed in:', user.email);
          this.updateLastLogin(user.uid);
        } else {
          console.log('🔥 Firebase Auth: User signed out');
        }
      });
    } else {
      console.log('🚫 Firebase auth disabled - running in local mode');
      // Immediately signal that auth initialization is complete (no user)
      setTimeout(() => {
        this.authStateListeners.forEach(listener => listener(null));
      }, 100);
    }
  }

  // Get current user
  getCurrentUser(): User | null {
    return this.currentUser;
  }

  // Get current user's ID token
  async getCurrentUserToken(): Promise<string | null> {
    if (!this.currentUser) return null;
    try {
      return await this.currentUser.getIdToken(true);
    } catch (error) {
      console.error('Failed to get user token:', error);
      return null;
    }
  }

  // Listen to auth state changes
  onAuthStateChanged(listener: (user: User | null) => void): () => void {
    this.authStateListeners.push(listener);
    return () => {
      const index = this.authStateListeners.indexOf(listener);
      if (index > -1) {
        this.authStateListeners.splice(index, 1);
      }
    };
  }

  // Email/Password Sign Up
  async signUpWithEmail(email: string, password: string, displayName: string, avatar: string): Promise<UserCredential> {
    if (!isFirebaseEnabled || !auth) {
      throw new Error('Authentication is currently disabled');
    }
    
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Update profile
      await updateProfile(user, {
        displayName,
        photoURL: avatar
      });

      // Create user profile in Firestore
      await this.createUserProfile(user.uid, {
        uid: user.uid,
        email: user.email!,
        displayName,
        avatar,
        createdAt: new Date(),
        lastLoginAt: new Date(),
        preferences: {
          soundEnabled: true,
          notificationsEnabled: true,
          theme: 'light',
          aiDifficulty: 'medium',
          aiPersonality: 'encouraging'
        },
        stats: {
          totalGames: 0,
          totalWins: 0,
          totalScore: 0,
          currentStreak: 0,
          longestStreak: 0,
          level: 1,
          experiencePoints: 0
        },
        achievements: []
      });

      console.log('✅ User signed up successfully:', user.email);
      return userCredential;
    } catch (error: any) {
      console.error('❌ Sign up error:', error);
      throw this.handleAuthError(error);
    }
  }

  // Email/Password Sign In
  async signInWithEmail(email: string, password: string): Promise<UserCredential> {
    if (!isFirebaseEnabled || !auth) {
      throw new Error('Authentication is currently disabled');
    }
    
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('✅ User signed in successfully:', userCredential.user.email);
      return userCredential;
    } catch (error: any) {
      console.error('❌ Sign in error:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Google sign-in — and, for a guest, an ACCOUNT UPGRADE rather than a swap.
   *
   * This used to call `signInWithPopup` unconditionally. For a signed-in
   * anonymous user that does not upgrade anything: Firebase mints a BRAND NEW
   * uid for the Google account and abandons the anonymous one. Everything keyed
   * to the old uid — every gameRecord that has them in `participantIds`, their
   * stats, their achievements, every friendship whose `uids` array names them —
   * stays behind, pointing at a uid nobody will ever sign in as again. The guest
   * who played all week, then signed in to "save" their progress, was the one
   * person guaranteed to lose it, and the app told them it had worked.
   *
   * `linkWithPopup` attaches the Google credential to the EXISTING anonymous
   * user, so the uid survives and every one of those references keeps resolving.
   *
   * The one case linking can't serve is when that Google account is already a
   * WordRush user (`credential-already-in-use`): two uids exist and Firebase
   * cannot merge them. We then sign in to the real account — the correct
   * outcome, since that account is the one with their actual history — and the
   * throwaway guest uid is what gets left behind, not their life's work. That
   * decision is reported in the returned `linkOutcome` so the caller can log the
   * conversion truthfully.
   */
  async signInWithGoogle(): Promise<UserCredential & { linkOutcome?: GoogleLinkOutcome }> {
    if (!isFirebaseEnabled || !auth || !googleProvider) {
      throw new Error('Authentication is currently disabled');
    }

    try {
      const guest = auth.currentUser?.isAnonymous ? auth.currentUser : null;
      const guestUid: string | null = guest?.uid ?? null;
      let linkOutcome: GoogleLinkOutcome = 'fresh-sign-in';
      let userCredential: UserCredential;

      if (guest) {
        try {
          userCredential = await linkWithPopup(guest, googleProvider);
          linkOutcome = 'linked-preserved-uid';
          console.log('🔗 Guest upgraded in place — uid preserved:', userCredential.user.uid);
        } catch (linkError: any) {
          const code = linkError?.code;
          if (code === 'auth/credential-already-in-use' || code === 'auth/email-already-in-use') {
            // That Google account is already somebody here. Sign into it.
            userCredential = await signInWithPopup(auth, googleProvider);
            linkOutcome = 'existing-account-signed-in';
            console.warn('🔀 Google account already exists — signed into it; guest uid discarded:', guestUid);
          } else {
            throw linkError;
          }
        }
      } else {
        userCredential = await signInWithPopup(auth, googleProvider);
      }

      const user = userCredential.user;

      // Check if user profile exists, create if not
      const userProfile = await this.getUserProfile(user.uid);
      if (!userProfile) {
        // Seed all four avatar fields from the reconciler rather than
        // shoving photoURL into `avatar` and losing it the first time the
        // setup screen saves a branded default.
        const av = reconcileAvatarFields(null, user.photoURL);
        await this.createUserProfile(user.uid, {
          uid: user.uid,
          email: user.email!,
          displayName: user.displayName || 'Anonymous Player',
          avatar: av.avatar,
          photoURL: av.photoURL,
          brandedAvatar: av.brandedAvatar,
          useGooglePhoto: av.useGooglePhoto,
          createdAt: new Date(),
          lastLoginAt: new Date(),
          accountType: 'human',
          isDemo: false,
          subscriptionTier: 'free',
          bio: '',
          privacy: defaultPrivacy(),
          preferences: {
            soundEnabled: true,
            notificationsEnabled: true,
            theme: 'light',
            aiDifficulty: 'medium',
            aiPersonality: 'encouraging'
          },
          stats: {
            totalGames: 0,
            totalWins: 0,
            totalScore: 0,
            currentStreak: 0,
            longestStreak: 0,
            level: 1,
            experiencePoints: 0
          },
          achievements: []
        });
      } else {
        // The linked case lands here: the profile already exists because it was
        // the GUEST'S profile, and it is now this person's real one. Attach the
        // email so the account stops looking anonymous, but keep the displayName
        // and avatar they chose — they picked those, and Google's "Arslan Saeed"
        // is not automatically an improvement on the name they play under.
        if (linkOutcome === 'linked-preserved-uid' && user.email) {
          await updateDoc(doc(db, 'users', user.uid), {
            email: user.email,
            accountType: 'human',
            upgradedFromGuestAt: new Date(),
            lastLoginAt: new Date(),
          }).catch(() => this.updateLastLogin(user.uid));
        } else {
          await this.updateLastLogin(user.uid);
        }
      }

      // The conversion moment. Fired here, not in the UI, because this is the
      // only place that knows whether the uid survived — and the whole value of
      // the metric is that it distinguishes an upgrade from a fresh account.
      if (linkOutcome !== 'fresh-sign-in' && guestUid) {
        void import('./growthService')
          .then(m => m.markGuestSignedIn({
            guestUid,
            finalUid: user.uid,
            uidPreserved: linkOutcome === 'linked-preserved-uid',
          }))
          .catch(() => { /* analytics is never load-bearing */ });
      }

      console.log('✅ Google sign in successful:', user.email);
      return Object.assign(userCredential, { linkOutcome });
    } catch (error: any) {
      console.error('❌ Google sign in error:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Anonymous sign-in for guests. Firebase mints a real uid, so the rest
   * of the app (websocket auth, gameRecords participantIds, profile loads)
   * works identically to a full account.
   *
   * A guest is a real account with a real history, and `signInWithGoogle()`
   * upgrades it IN PLACE via linkWithPopup — the uid survives, so the history
   * does too. See the comment there.
   *
   * Profile is created with a placeholder displayName + emoji avatar; the
   * setup screen lets the user pick a real name and face.
   */
  async signInAnonymously(): Promise<UserCredential> {
    if (!isFirebaseEnabled || !auth) {
      throw new Error('Authentication is currently disabled');
    }
    try {
      const userCredential = await signInAnonymously(auth);
      const user = userCredential.user;

      // "Does a profile exist" must mean "does it have a NAME", not "does a
      // document exist at this path".
      //
      // This is why every guest showed up as "Player". signInAnonymously()
      // flips the auth state, which synchronously fires the onAuthStateChanged
      // listener in this class's constructor, which calls updateLastLogin(),
      // which does setDoc({lastLoginAt}, {merge:true}) — and that CREATES the
      // document. By the time we get here and ask "is there a profile?", there
      // is: a document containing nothing but a timestamp. So we skipped
      // createUserProfile, the guest never got a displayName or an avatar, and
      // every downstream fallback rendered them as the literal string "Player".
      const existing = await this.getUserProfile(user.uid).catch(() => null);
      if (!existing?.displayName) {
        const guestName = generateGuestName();
        try {
          // Update the Firebase user's displayName so currentUser.displayName
          // is something better than the empty default.
          await updateProfile(user, { displayName: guestName });
        } catch {}
        await this.createUserProfile(user.uid, {
          uid: user.uid,
          email: '', // anonymous — no email
          displayName: guestName,
          avatar: 'fox', // a brand avatar; setup screen lets them swap
          createdAt: new Date(),
          lastLoginAt: new Date(),
          accountType: 'human',
          isDemo: false,
          subscriptionTier: 'free',
          bio: '',
          privacy: defaultPrivacy(),
          preferences: {
            soundEnabled: true,
            notificationsEnabled: true,
            theme: 'light',
            aiDifficulty: 'medium',
            aiPersonality: 'encouraging',
          },
          stats: {
            totalGames: 0,
            totalWins: 0,
            totalScore: 0,
            currentStreak: 0,
            longestStreak: 0,
            level: 1,
            experiencePoints: 0,
          },
          achievements: [],
        });
      } else {
        await this.updateLastLogin(user.uid);
      }
      console.log('✅ Anonymous (guest) sign in successful:', user.uid);
      return userCredential;
    } catch (error: any) {
      console.error('❌ Anonymous sign in error:', error);
      throw this.handleAuthError(error);
    }
  }

  // Sign Out
  async signOut(): Promise<void> {
    try {
      await signOut(auth);
      console.log('✅ User signed out successfully');
    } catch (error: any) {
      console.error('❌ Sign out error:', error);
      throw this.handleAuthError(error);
    }
  }

  // Password Reset
  async resetPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(auth, email);
      console.log('✅ Password reset email sent to:', email);
    } catch (error: any) {
      console.error('❌ Password reset error:', error);
      throw this.handleAuthError(error);
    }
  }

  // Get user profile from Firestore
  async getUserProfile(uid: string): Promise<UserProfile | null> {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        return userDoc.data() as UserProfile;
      }
      return null;
    } catch (error: any) {
      console.error('❌ Error getting user profile:', error);
      
      // Log specific Firestore errors
      if (error.code === 'permission-denied') {
        console.warn('⚠️ Firestore permission denied - check security rules');
      } else if (error.code === 'unavailable') {
        console.warn('⚠️ Firestore temporarily unavailable');
      }
      
      return null;
    }
  }

  // Create user profile in Firestore
  async createUserProfile(uid: string, profile: UserProfile): Promise<void> {
    try {
      // `merge: true` because this is called from a path that fires when a
      // guest's doc merely LACKS a displayName — not only when the doc is
      // absent. A plain setDoc there doesn't add a name to a profile, it
      // replaces the profile: career_score, ranked_score and every game the
      // guest has played, gone. Creating an identity must never be able to
      // erase a record. (The rules now refuse that write outright; this makes
      // sure we never ask.)
      await setDoc(doc(db, 'users', uid), profile, { merge: true });
      console.log('✅ User profile created for:', uid);
    } catch (error: any) {
      console.error('❌ Error creating user profile:', error);
      
      // Check if it's a Firestore permission error
      if (error.code === 'permission-denied') {
        console.warn('⚠️ Firestore permission denied - check security rules');
      } else if (error.code === 'unavailable') {
        console.warn('⚠️ Firestore temporarily unavailable - user can still continue');
      } else {
        console.error('💥 Unexpected Firestore error:', error.code, error.message);
      }
      
      // Don't throw error - allow user to continue with basic authentication
      // The profile will be created later when Firestore becomes available
    }
  }

  // Update user profile
  async updateUserProfile(uid: string, updates: Partial<UserProfile>): Promise<void> {
    try {
      await updateDoc(doc(db, 'users', uid), updates);
      console.log('✅ User profile updated for:', uid);
    } catch (error) {
      console.error('❌ Error updating user profile:', error);
      throw error;
    }
  }

  // Update last login time. We use setDoc + merge so the very first
  // anonymous-auth call doesn't crash with "No document to update":
  // onAuthStateChanged fires before our signInAnonymously path has
  // created the profile. This way we either create-with-merge on first
  // touch, or merge into the existing doc.
  async updateLastLogin(uid: string): Promise<void> {
    try {
      await setDoc(doc(db, 'users', uid), {
        lastLoginAt: new Date(),
      }, { merge: true });
    } catch (error) {
      // Permission / offline errors are non-fatal — sign-in continues.
      console.warn('updateLastLogin (non-blocking):', (error as any)?.message || error);
    }
  }

  // Handle Firebase auth errors
  private handleAuthError(error: any): Error {
    let message = 'An unexpected error occurred';
    
    switch (error.code) {
      case 'auth/user-not-found':
        message = 'No account found with this email address';
        break;
      case 'auth/wrong-password':
        message = 'Incorrect password';
        break;
      case 'auth/email-already-in-use':
        message = 'An account with this email already exists';
        break;
      case 'auth/weak-password':
        message = 'Password should be at least 6 characters';
        break;
      case 'auth/invalid-email':
        message = 'Please enter a valid email address';
        break;
      case 'auth/too-many-requests':
        message = 'Too many failed attempts. Please try again later';
        break;
      case 'auth/popup-closed-by-user':
        message = 'Sign in was cancelled';
        break;
      case 'auth/popup-blocked':
        message = 'Sign in popup was blocked. Please allow popups for this site';
        break;
      case 'auth/network-request-failed':
        message = 'Network error. Please check your connection';
        break;
      default:
        message = error.message || 'Authentication failed';
    }
    
    return new Error(message);
  }
}

/** True for a remote image URL (a Google photo), false for a branded id. */
export function looksLikePhotoUrl(v?: string | null): boolean {
  return !!v && /^https?:\/\//i.test(v.trim());
}

export interface AvatarFields {
  avatar: string;
  photoURL?: string;
  brandedAvatar: string;
  useGooglePhoto: boolean;
}

/**
 * The single source of truth for "what avatar does this user actually have".
 *
 * Call it on every login with the live Firebase user, and whenever the user
 * changes their pick. It reconciles the four fields from whatever partial,
 * legacy shape is currently in Firestore:
 *
 *   - photoURL       ← the live Google photo, refreshed every login
 *   - brandedAvatar  ← their branded pick, recovered from a legacy `avatar`
 *   - useGooglePhoto ← their preference; defaults to ON when a photo exists
 *   - avatar         ← the DERIVED effective value everything renders
 *
 * Legacy shapes it has to survive:
 *   avatar: 'fox'                  → branded pick, photo shadowed (the bug)
 *   avatar: '👤' / '🎮'            → a dead default nobody chose
 *   avatar: 'https://lh3...'       → photo already in the overloaded field
 *   avatar: undefined              → brand-new profile
 */
export function reconcileAvatarFields(
  profile: Partial<UserProfile> | null,
  livePhotoURL?: string | null,
): AvatarFields {
  const stored = (profile?.avatar || '').trim();

  // The photo: prefer the live one from the Google account (it can rotate),
  // fall back to what we mirrored last time, and finally rescue one that's
  // stuck in the overloaded legacy `avatar` field.
  const photoURL =
    (livePhotoURL || '').trim() ||
    (profile?.photoURL || '').trim() ||
    (looksLikePhotoUrl(stored) ? stored : '') ||
    undefined;

  // The branded pick: an explicit one wins. Otherwise recover it from the
  // legacy `avatar` — but only if it's actually a branded id. Emoji ('👤',
  // '🎮') were never a real choice, they were a default, so they don't
  // count as a pick and we don't preserve them.
  const legacyIsBrandId = /^[a-z][a-z0-9_-]{2,15}$/i.test(stored) && !looksLikePhotoUrl(stored);
  const brandedAvatar =
    (profile?.brandedAvatar || '').trim() ||
    (legacyIsBrandId ? stored : '') ||
    'fox';

  // The preference. `undefined` means the user has never been asked — and
  // if they signed in with Google, showing their face is the answer they
  // expect. Only an explicit `false` opts out.
  const useGooglePhoto = profile?.useGooglePhoto ?? !!photoURL;

  const avatar = useGooglePhoto && photoURL ? photoURL : brandedAvatar;

  return { avatar, photoURL, brandedAvatar, useGooglePhoto };
}

/**
 * Privacy defaults — shared by the guest/Google sign-up paths and
 * exposed on the Settings → Privacy "Reset to defaults" button. Tuned
 * for "private by default for sensitive bits, public by default for
 * the social loop that makes the game alive."
 */
export function defaultPrivacy(): NonNullable<UserProfile['privacy']> {
  return {
    showOnlineToPublic: true,    // friends + community see you online
    showOnLeaderboard: true,     // top-10 leaderboard
    defaultGameVisibility: 'private', // safer default — opt in to public
    publicizeMyGames: true,      // your wins show on Recent Games feed
    allowFriendRequests: true,   // anyone can request; you accept/decline
  };
}

/**
 * Adjective + noun guest names. Light, family-friendly, easy to recognize
 * at a glance. Assigned once at guest creation; the Profile screen lets the
 * user rename or re-roll.
 *
 * Exported because the Profile screen's "shuffle" button has to produce
 * names from the SAME pool the server picked from — otherwise a guest who
 * re-rolls drifts into a different naming scheme than everyone else in the
 * lobby, and "Witty Fox" starts sitting next to "Player 3".
 */
export const GUEST_ADJECTIVES = ['Witty', 'Sunny', 'Mighty', 'Quick', 'Lucky', 'Brave', 'Cosmic', 'Mellow', 'Plucky', 'Cheery', 'Sharp', 'Bold'];
export const GUEST_NOUNS = ['Fox', 'Otter', 'Pine', 'Tide', 'Plum', 'Honey', 'Mint', 'Rose', 'Iris', 'Spark', 'Cherry', 'Sage'];

export function generateGuestName(): string {
  const a = GUEST_ADJECTIVES[Math.floor(Math.random() * GUEST_ADJECTIVES.length)];
  const n = GUEST_NOUNS[Math.floor(Math.random() * GUEST_NOUNS.length)];
  return `${a} ${n}`;
}

// Create and export singleton instance
export const firebaseAuth = new FirebaseAuthService();

// Export Firebase instances for direct use if needed
export { auth, db, app }; 